import React from 'react';
import { useFormContext, FieldError } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import type { Form } from '../../interfaces/form.interface';
import type { Metadata } from '../../interfaces/metadata.interface';
import { BaseField } from './BaseField';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../utils/cn';

interface SelectFieldProps {
  meta: Metadata;
  index: number;
  configuration: Form;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  meta,
  index,
  configuration
}) => {
  const { setValue, formState: { errors }, watch } = useFormContext();
  const error = errors[meta.id] as FieldError | undefined;

  const isMulti = !!meta.definition?.multiselect;
  const maxOptions = meta.definition?.maxOptions ?? null;

  const rawValue = watch(meta.id) as unknown;
  const selectedValues: string[] = React.useMemo(() => {
    if (!isMulti) return [];
    if (Array.isArray(rawValue)) return rawValue as string[];
    if (typeof rawValue === 'string') {
      if (rawValue.trim() === '') return [];
      return [rawValue];
    }
    return [];
  }, [rawValue, isMulti]);

  const handleValueChange = (value: string) => {
    setValue(meta.id, value);
  };

  const handleToggle = (option: string) => {
    if (!isMulti) return;
    const exists = selectedValues.includes(option);
    let next: string[];
    if (exists) {
      next = selectedValues.filter((o) => o !== option);
    } else {
      // Enforce max options if set
      if (typeof maxOptions === 'number' && maxOptions !== null && selectedValues.length >= maxOptions) {
        return;
      }
      next = [...selectedValues, option];
    }
    setValue(meta.id, next, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <BaseField
      meta={meta}
      index={index}
      error={error}
      configuration={configuration}
    >
      {!isMulti ? (
        <Select onValueChange={handleValueChange}>
          <SelectTrigger className={error ? 'border-red-500' : ''}>
            <SelectValue placeholder={meta.description} />
          </SelectTrigger>
          <SelectContent>
            {meta.definition?.options?.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex h-10 w-full items-center justify-between rounded-md border border-input !bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 focus:border-blue-500/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 text-left",
                error ? 'border-red-500' : undefined
              )}
            >
              <span className={cn("line-clamp-1", selectedValues.length ? "" : "text-muted-foreground")}>
                {selectedValues.length
                  ? selectedValues.join(', ')
                  : (meta.description || 'Seleccione...')}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-select-trigger-width,_18rem)] p-2">
            <div className="max-h-64 overflow-auto space-y-1">
              {meta.definition?.options?.map((option) => {
                const checked = selectedValues.includes(option);
                const atLimit = typeof maxOptions === 'number'
                  && maxOptions !== null
                  && selectedValues.length >= maxOptions
                  && !checked;
                return (
                  <label
                    key={option}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer select-none hover:bg-gray-100",
                      atLimit ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!atLimit) {
                        handleToggle(option);
                      }
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => handleToggle(option)}
                      disabled={atLimit}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                );
              })}
            </div>
            {typeof maxOptions === 'number' && maxOptions !== null && (
              <p className="mt-2 text-xs text-muted-foreground">
                Máximo {maxOptions} opción{maxOptions === 1 ? '' : 'es'}.
              </p>
            )}
          </PopoverContent>
        </Popover>
      )}
    </BaseField>
  );
}; 